import clsx from 'clsx'
import { useMemo, useRef, type ReactNode } from 'react'
import { TextField, type AutocompleteRenderInputParams } from '@mui/material'
import SearchIcon from '@mui/icons-material/Search'
import { useSearchInputKeyboardNavigation } from './useSearchInputKeyboardNavigation'
import styles from './SearchInput.module.scss'

type SearchInputProps = {
  params: AutocompleteRenderInputParams
  suggestion?: string
  placeholder?: string
  endAdornment?: ReactNode
  showEndAdornment?: boolean
  onSearch?: () => void
  onSearchTab?: () => void
  onSearchEnter?: () => void
}

export const SearchInput = (props: SearchInputProps) => {
  const {
    params,
    suggestion,
    placeholder,
    showEndAdornment,
    endAdornment,
    onSearch,
    onSearchTab,
    onSearchEnter,
  } = props

  const inputRef = useRef<HTMLInputElement>(null)
  useSearchInputKeyboardNavigation(inputRef)

  const suggestionSuffix = useMemo(() => {
    if (!suggestion || !params.inputProps.value) return ''
    const inputString = String(params.inputProps.value)
    if (suggestion.startsWith(inputString)) {
      return suggestion.slice(inputString.length)
    }
    return ''
  }, [suggestion, params.inputProps.value])

  return (
    <div id="searchInputWrapper" className={styles.wrapper}>
      {suggestion && (
        <div className={styles.suggestion}>
          <span className={styles.highlighted}>{params.inputProps.value}</span>
          <span className={styles.dimmed}>{suggestionSuffix}</span>
        </div>
      )}
      <TextField
        {...params}
        inputRef={inputRef}
        placeholder={placeholder}
        classes={{
          root: clsx(styles.inputRoot, {
            [styles.withAdornment]: showEndAdornment,
            [styles.withSuggestion]: suggestion,
          }),
        }}
        slotProps={{
          htmlInput: {
            ...params.inputProps,
            'data-track-id': 'searchBox',
          },
          input: {
            ...params.InputProps,
            endAdornment,
            onKeyDown: (e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                onSearchEnter?.()
              }
              if (e.key === 'Tab') {
                e.preventDefault()
                onSearchTab?.()
              }
            },
          },
        }}
      />
      <div
        className={styles.searchIcon}
        data-track-id="searchButton"
        onClick={(e) => {
          e.preventDefault()
          onSearch?.()
        }}
      >
        <SearchIcon />
      </div>
    </div>
  )
}
